import React from "react";
import {
  ChevronDown,
  CandlestickChart,
  FlaskConical,
  LucideLayoutDashboard,
} from "lucide-react";
import { useModal } from "@/components/Modals/ModalContext";
import { usePathname, useRouter } from "next/navigation";
import Link from "next/link";
import { IDX_EXCHANGE_URL } from "@/config/idxExchangeAppConfig";
import { IDX_LAUNCHLAB_LIVE_PATH } from "@/config/idxLaunchlabAppConfig";
import { useLaunchlabNav } from "@/hooks/useLaunchlabNav";

const PENDING_AUTH_REDIRECT_KEY = "idx_pending_auth_redirect";

export default function AIStatusIndicator({ isAuthenticated }) {
  const { openModal } = useModal();
  const pathname = usePathname();
  const router = useRouter();
  const { onLaunchlabClick, warmLaunchlab, isLaunchlabRoute, isLaunchlabLivePage } =
    useLaunchlabNav();
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const mobileNavRef = React.useRef(null);

  const chip =
    "flex shrink-0 items-center gap-1.5 whitespace-nowrap px-2.5 py-1.5 bg-gray-800/90 border border-gray-700 rounded-lg text-gray-200 lg:gap-2 lg:px-3";
  const chipInteractive =
    `${chip} group transition-colors hover:bg-gray-800/60 hover:border-gray-600 hover:text-white`;
  const chipActive =
    `${chipInteractive} border-emerald-500/40 bg-emerald-950/30 text-emerald-100`;

  const isDashboardRoute = pathname === "/dashboard";
  const dashIconClass = isDashboardRoute
    ? "w-3.5 h-3.5 text-emerald-300"
    : "w-3.5 h-3.5 text-gray-400 transition-colors group-hover:text-white";
  const launchlabIconClass = isLaunchlabRoute
    ? "w-3.5 h-3.5 text-emerald-300"
    : "w-3.5 h-3.5 text-gray-400 transition-colors group-hover:text-white";

  const mobileNavLabel = isDashboardRoute
    ? "Bot Dashboard"
    : isLaunchlabRoute
      ? "Launchlab"
      : "Go to";

  const openLoginForDashboard = React.useCallback(() => {
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(PENDING_AUTH_REDIRECT_KEY, "/dashboard");
    }
    openModal("login");
  }, [openModal]);

  const closeMobileNav = React.useCallback(() => {
    setMobileNavOpen(false);
  }, []);

  React.useEffect(() => {
    if (!isAuthenticated || typeof window === "undefined") return;
    const pending = window.sessionStorage.getItem(PENDING_AUTH_REDIRECT_KEY);
    if (!pending) return;
    window.sessionStorage.removeItem(PENDING_AUTH_REDIRECT_KEY);
    if (pending !== pathname) {
      router.push(pending);
    }
  }, [isAuthenticated, pathname, router]);

  React.useEffect(() => {
    if (!mobileNavOpen) return;
    const onDoc = (e) => {
      if (mobileNavRef.current?.contains(e.target)) return;
      setMobileNavOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMobileNavOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [mobileNavOpen]);

  const mobileMenuItemClass =
    "flex w-full items-center justify-start gap-1.5 px-3 py-2 text-left text-xs font-medium text-gray-200 transition-colors hover:bg-gray-800/80 hover:text-white whitespace-nowrap";

  return (
    <>
      <div className="hidden md:flex items-center gap-2 lg:gap-4 flex-1 justify-center min-w-0 max-w-xl">
        {isAuthenticated ? (
          <Link
            href="/dashboard"
            className={isDashboardRoute ? chipActive : chipInteractive}
            aria-current={isDashboardRoute ? "page" : undefined}
            rel="noopener noreferrer"
          >
            <LucideLayoutDashboard className={dashIconClass} />
            <span className="text-xs font-medium">Bot Dashboard</span>
          </Link>
        ) : (
          <button
            type="button"
            onClick={openLoginForDashboard}
            className={isDashboardRoute ? chipActive : chipInteractive}
          >
            <LucideLayoutDashboard className={dashIconClass} />
            <span className="text-xs font-medium">Bot Dashboard</span>
          </button>
        )}
        <a
          href={IDX_EXCHANGE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={chipInteractive}
          aria-label="Open Exchange"
        >
          <CandlestickChart className="w-3.5 h-3.5 text-gray-400 transition-colors group-hover:text-white" />
          <span className="text-xs font-medium">Exchange</span>
        </a>
        <Link
          href={IDX_LAUNCHLAB_LIVE_PATH}
          prefetch
          onClick={onLaunchlabClick}
          onMouseEnter={warmLaunchlab}
          onFocus={warmLaunchlab}
          className={isLaunchlabRoute ? chipActive : chipInteractive}
          aria-label="Go to IDX Launchlab live feed"
          aria-current={isLaunchlabLivePage ? "page" : undefined}
          rel="noopener noreferrer"
        >
          <FlaskConical className={launchlabIconClass} />
          <span className="text-xs font-medium">Launchlab</span>
        </Link>
      </div>

      <div
        ref={mobileNavRef}
        className="relative flex min-w-fit flex-1 justify-center md:hidden"
      >
        <div className="relative inline-flex flex-col items-center">
          <button
            type="button"
            onClick={() => setMobileNavOpen((open) => !open)}
            className={`inline-flex items-center gap-1 rounded-md border px-2 py-1.5 text-xs font-medium transition-colors sm:gap-1.5 sm:px-2.5 ${
              isDashboardRoute || isLaunchlabRoute
                ? "border-emerald-500/40 bg-emerald-950/30 text-emerald-100"
                : "border-gray-700 bg-gray-800/90 text-gray-200 hover:border-gray-600 hover:text-white"
            }`}
            aria-haspopup="menu"
            aria-expanded={mobileNavOpen}
            aria-label="Open navigation menu"
          >
            <span className="truncate">{mobileNavLabel}</span>
            <ChevronDown
              className={`h-3.5 w-3.5 shrink-0 transition-transform ${mobileNavOpen ? "rotate-180" : ""}`}
              aria-hidden
            />
          </button>

          {mobileNavOpen ? (
            <div
              role="menu"
              className="absolute top-[calc(100%+0.35rem)] left-1/2 z-[110] w-max min-w-full -translate-x-1/2 overflow-hidden rounded-lg border border-gray-700 bg-gray-900/95 py-1 shadow-xl backdrop-blur-xl"
            >
              {isAuthenticated ? (
                <Link
                  href="/dashboard"
                  role="menuitem"
                  onClick={closeMobileNav}
                  className={`${mobileMenuItemClass} ${
                    isDashboardRoute ? "bg-emerald-950/40 text-emerald-100" : ""
                  }`}
                  rel="noopener noreferrer"
                >
                  <LucideLayoutDashboard
                    className={`h-3.5 w-3.5 shrink-0 ${isDashboardRoute ? "text-emerald-300" : "text-gray-400"}`}
                  />
                  Bot Dashboard
                </Link>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    closeMobileNav();
                    openLoginForDashboard();
                  }}
                  className={mobileMenuItemClass}
                >
                  <LucideLayoutDashboard
                    className={`h-3.5 w-3.5 shrink-0 ${isDashboardRoute ? "text-emerald-300" : "text-gray-400"}`}
                  />
                  Bot Dashboard
                </button>
              )}
              <a
                href={IDX_EXCHANGE_URL}
                target="_blank"
                rel="noopener noreferrer"
                role="menuitem"
                onClick={closeMobileNav}
                className={mobileMenuItemClass}
              >
                <CandlestickChart className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                Exchange
              </a>
              <Link
                href={IDX_LAUNCHLAB_LIVE_PATH}
                prefetch
                role="menuitem"
                onClick={(e) => {
                  closeMobileNav();
                  onLaunchlabClick(e);
                }}
                onMouseEnter={warmLaunchlab}
                onFocus={warmLaunchlab}
                className={`${mobileMenuItemClass} ${
                  isLaunchlabRoute ? "bg-emerald-950/40 text-emerald-100" : ""
                }`}
                aria-current={isLaunchlabLivePage ? "page" : undefined}
                rel="noopener noreferrer"
              >
                <FlaskConical
                  className={`h-3.5 w-3.5 shrink-0 ${isLaunchlabRoute ? "text-emerald-300" : "text-gray-400"}`}
                />
                Launchlab
              </Link>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
